import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import {
    AchievementEarnedNotificationPayload,
    CollectionSuggestionApprovedPayload,
    CollectionSuggestionCreatedPayload,
    CollectionSuggestionRejectedPayload,
    CommentUpvotedNotificationPayload,
    EnoughGobitsAvatarNotificationPayload,
    FlagDeletedNotificationPayload,
    Notification,
    NotificationTypeEnum,
    ProblemFlagMinimal,
    SuggestedProblemEditApprovedPayload,
    SuggestedProblemEditCreatedPayload,
    SuggestedProblemEditRejectedPayload,
    TournamentCompletedNotificationPayload,
    TournamentMinimal,
    UserFriendshipRequestNotificationPayload,
    UserMentionedInCommentPayload,
    UserMentionedInScenarioCommentPayload,
    UserMinimal,
} from '@types';

const AchievementEarnedNotification = ({
    name,
    code,
}: {
    name: string;
    code: string;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/achievements/${code}`}>
            {t('notifications.achievement_earned', {achievementName: name})}
        </Link>
    );
};

const CollectionSuggestionApprovedNotification = ({
    collectionName,
    resolvedBy,
}: {
    collectionName: string;
    resolvedBy: string;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to="/dashboard/suggestions?tab=collection">
            {t('notifications.collection_suggestion_approved', {
                collectionName,
                resolvedBy,
            })}
        </Link>
    );
};

const CollectionSuggestionCreatedNotification = ({
    collectionId,
    collectionName,
    problemId,
}: {
    collectionId: number;
    collectionName: string;
    problemId: number;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/collections/${collectionId}?tab=suggestions`}>
            {t('notifications.collection_suggestion_created', {
                collectionName,
                problemId,
            })}
        </Link>
    );
};

const CollectionSuggestionRejectedNotification = ({
    collectionName,
    resolvedBy,
    resolutionComment,
}: {
    collectionName: string;
    resolvedBy: string;
    resolutionComment: string;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to="/dashboard/suggestions?tab=collection">
            {t('notifications.collection_suggestion_rejected', {
                collectionName,
                resolvedBy,
            })}
            {resolutionComment && (
                <span className="text-muted d-block text-sm">
                    {t('common.reason')}: {resolutionComment}
                </span>
            )}
        </Link>
    );
};

const CommentUpvotedNotification = ({
    commentId,
    problemId,
    compressedPath,
    voter,
    commentExcerpt,
}: {
    commentId: number;
    problemId: number;
    compressedPath: string;
    voter: UserMinimal;
    commentExcerpt: string;
}) => {
    const {t} = useTranslation();

    const problemUrl = `/problems/${problemId}#comment-${compressedPath}-${commentId}`;

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={problemUrl}
            title={commentExcerpt}>
            {t('notifications.comment_upvoted', {
                userName: voter.name,
                commentExcerpt:
                    commentExcerpt.length > 50
                        ? `${commentExcerpt.substring(0, 50)}...`
                        : commentExcerpt,
            })}
        </Link>
    );
};

const EnoughGobitsAvatarNotification = ({gobits}: {gobits: number}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to="/settings/avatar">
            {t('notifications.enough_gobits_avatar', {gobits: gobits})}
        </Link>
    );
};

const FlagDeletedNotification = ({
    user,
    flag,
    reason,
}: {
    user: UserMinimal;
    flag: ProblemFlagMinimal;
    reason: string | null;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/problems/${flag.problem.id}`}>
            {t('notifications.flag_deleted', {
                userName: user.name,
                flagName: flag.flagName,
                problemId: flag.problem.id,
                reason: reason ?? '',
            })}
        </Link>
    );
};

const SuggestedProblemEditApprovedNotification = ({
    suggestionId,
    resolvedBy,
}: {
    suggestionId: number;
    resolvedBy: string;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/suggestions/${suggestionId}`}>
            {t('notifications.suggested_problem_edit_approved', {
                id: suggestionId,
                resolvedBy,
            })}
        </Link>
    );
};

const SuggestedProblemEditCreatedNotification = ({
    suggestionId,
    problemId,
}: {
    suggestionId: number;
    problemId: number;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/suggestions/${suggestionId}`}>
            {t('notifications.suggested_problem_edit_created', {problemId})}
        </Link>
    );
};

const SuggestedProblemEditRejectedNotification = ({
    suggestionId,
    resolutionComment,
}: {
    suggestionId: number;
    resolutionComment: string;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/suggestions/${suggestionId}`}>
            {t('notifications.suggested_problem_edit_rejected', {
                id: suggestionId,
            })}
            {resolutionComment && (
                <span className="text-muted d-block text-sm">
                    {t('common.reason')}: {resolutionComment}
                </span>
            )}
        </Link>
    );
};

const TournamentCompletedNotification = ({
    tournament,
}: {
    tournament: TournamentMinimal;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={`/tournaments/${tournament.id}/results`}>
            {t('notifications.tournament_completed')}
        </Link>
    );
};

const UserFriendshipRequestAcceptedNotification = ({
    user,
}: {
    user: {name: string};
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to="/dashboard/friends">
            {t('notifications.user_friendship_request_accepted', {
                userName: user.name,
            })}
        </Link>
    );
};

const UserFriendshipRequestSentNotification = ({
    user,
}: {
    user: UserMinimal;
}) => {
    const {t} = useTranslation();

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to="/friendship-requests">
            {t('notifications.user_friendship_request_sent', {
                userName: user.name,
            })}
        </Link>
    );
};

const UserMentionedInCommentNotification = ({
    commentId,
    problemId,
    compressedPath,
    mentioner,
    commentExcerpt,
}: {
    commentId: number;
    problemId: number;
    compressedPath: string;
    mentioner: UserMinimal;
    commentExcerpt: string;
}) => {
    const {t} = useTranslation();

    const problemUrl = `/problems/${problemId}#comment-${compressedPath}-${commentId}`;

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={problemUrl}
            title={commentExcerpt}>
            {t('notifications.user_mentioned_in_comment', {
                userName: mentioner.name,
                commentExcerpt:
                    commentExcerpt.length > 50
                        ? `${commentExcerpt.substring(0, 50)}...`
                        : commentExcerpt,
            })}
        </Link>
    );
};

const UserMentionedInScenarioCommentNotification = ({
    scenarioId,
    mentioner,
    commentExcerpt,
}: {
    scenarioId: number;
    mentioner: UserMinimal;
    commentExcerpt: string;
}) => {
    const {t} = useTranslation();

    const scenarioUrl = `/invasions/${scenarioId}`;

    return (
        <Link
            className="list-group-item list-group-item-action px-2"
            to={scenarioUrl}
            title={commentExcerpt}>
            {t('notifications.user_mentioned_in_scenario_comment', {
                userName: mentioner.name,
                commentExcerpt:
                    commentExcerpt.length > 50
                        ? `${commentExcerpt.substring(0, 50)}...`
                        : commentExcerpt,
            })}
        </Link>
    );
};

export const NotificationItem = ({
    notification: n,
    username,
}: {
    notification: Notification;
    username?: string;
}) => {
    if (n.type === NotificationTypeEnum.USER_FRIENDSHIP_REQUEST_SENT) {
        return (
            <UserFriendshipRequestSentNotification
                user={
                    (n.payload as UserFriendshipRequestNotificationPayload).user
                }
            />
        );
    }
    if (n.type === NotificationTypeEnum.USER_FRIENDSHIP_REQUEST_ACCEPTED) {
        return (
            <UserFriendshipRequestAcceptedNotification
                user={
                    (n.payload as UserFriendshipRequestNotificationPayload).user
                }
            />
        );
    }
    if (n.type === NotificationTypeEnum.USER_MENTIONED_IN_COMMENT) {
        const payload = n.payload as UserMentionedInCommentPayload;
        return (
            <UserMentionedInCommentNotification
                commentId={payload.commentId}
                problemId={payload.problemId}
                compressedPath={payload.compressedPath}
                mentioner={payload.mentioner}
                commentExcerpt={payload.commentExcerpt}
            />
        );
    }
    if (n.type === NotificationTypeEnum.USER_MENTIONED_IN_SCENARIO_COMMENT) {
        const payload = n.payload as UserMentionedInScenarioCommentPayload;
        return (
            <UserMentionedInScenarioCommentNotification
                scenarioId={payload.scenarioId}
                mentioner={payload.mentioner}
                commentExcerpt={payload.commentExcerpt}
            />
        );
    }
    if (n.type === NotificationTypeEnum.COMMENT_UPVOTED) {
        const payload = n.payload as CommentUpvotedNotificationPayload;
        return (
            <CommentUpvotedNotification
                commentId={payload.commentId}
                problemId={payload.problemId}
                compressedPath={payload.compressedPath}
                voter={payload.voter}
                commentExcerpt={payload.commentExcerpt}
            />
        );
    }
    if (username && n.type === NotificationTypeEnum.ACHIEVEMENT_EARNED) {
        return (
            <AchievementEarnedNotification
                name={(n.payload as AchievementEarnedNotificationPayload).name}
                code={(n.payload as AchievementEarnedNotificationPayload).code}
            />
        );
    }

    if (n.type === NotificationTypeEnum.FLAG_DELETED) {
        const payload = n.payload as FlagDeletedNotificationPayload;
        return (
            <FlagDeletedNotification
                user={payload.user}
                flag={payload.flag}
                reason={payload.reason}
            />
        );
    }

    if (n.type === NotificationTypeEnum.ENOUGH_GOBITS_AVATAR) {
        return (
            <EnoughGobitsAvatarNotification
                gobits={
                    (n.payload as EnoughGobitsAvatarNotificationPayload).gobits
                }
            />
        );
    }

    if (n.type === NotificationTypeEnum.SUGGESTED_PROBLEM_EDIT_CREATED) {
        return (
            <SuggestedProblemEditCreatedNotification
                suggestionId={
                    (n.payload as SuggestedProblemEditCreatedPayload).id
                }
                problemId={
                    (n.payload as SuggestedProblemEditCreatedPayload).problemId
                }
            />
        );
    }

    if (n.type === NotificationTypeEnum.SUGGESTED_PROBLEM_EDIT_APPROVED) {
        return (
            <SuggestedProblemEditApprovedNotification
                suggestionId={
                    (n.payload as SuggestedProblemEditApprovedPayload).id
                }
                resolvedBy={
                    (n.payload as SuggestedProblemEditApprovedPayload)
                        .resolvedBy.name
                }
            />
        );
    }

    if (n.type === NotificationTypeEnum.SUGGESTED_PROBLEM_EDIT_REJECTED) {
        return (
            <SuggestedProblemEditRejectedNotification
                suggestionId={
                    (n.payload as SuggestedProblemEditApprovedPayload).id
                }
                resolutionComment={
                    (n.payload as SuggestedProblemEditRejectedPayload)
                        .resolutionComment
                }
            />
        );
    }

    if (n.type === NotificationTypeEnum.TOURNAMENT_COMPLETED) {
        return (
            <TournamentCompletedNotification
                tournament={
                    (n.payload as TournamentCompletedNotificationPayload)
                        .tournament
                }
            />
        );
    }

    if (n.type === NotificationTypeEnum.COLLECTION_SUGGESTION_CREATED) {
        const payload = n.payload as CollectionSuggestionCreatedPayload;
        return (
            <CollectionSuggestionCreatedNotification
                collectionId={payload.collectionId}
                collectionName={payload.collectionName}
                problemId={payload.problemId}
            />
        );
    }

    if (n.type === NotificationTypeEnum.COLLECTION_SUGGESTION_APPROVED) {
        const payload = n.payload as CollectionSuggestionApprovedPayload;
        return (
            <CollectionSuggestionApprovedNotification
                collectionName={payload.collectionName}
                resolvedBy={payload.resolvedBy.name}
            />
        );
    }

    if (n.type === NotificationTypeEnum.COLLECTION_SUGGESTION_REJECTED) {
        const payload = n.payload as CollectionSuggestionRejectedPayload;
        return (
            <CollectionSuggestionRejectedNotification
                collectionName={payload.collectionName}
                resolvedBy={payload.resolvedBy.name}
                resolutionComment={payload.resolutionComment ?? ''}
            />
        );
    }

    return null;
};
